import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, addDoc, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import './AddFoodItemPage.css'; // Make sure you have created this CSS file
// ================================================================
// === 1. Import useAuth to check for permissions               ===
// ================================================================
import { useAuth } from '../auth/AuthContext';

function AddFoodItemPage() {
  const navigate = useNavigate();
  const { foodItemId } = useParams();
  const isEditing = Boolean(foodItemId);

  // ================================================================
  // === 2. Get the hasPermission function from the context       ===
  // ================================================================
  const { hasPermission } = useAuth();

  // Form State
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [coins, setCoins] = useState('');
  const [selectedCategory, setSelectedCategory] = useState({ id: '', name: '' });
  const [categories, setCategories] = useState([]);

  // UI State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch all categories for the dropdown (no change here)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'categories'));
        setCategories(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error("Error fetching categories:", err);
        setError("Failed to load categories from the database.");
      }
    };
    fetchCategories();
  }, []);

  // Fetch existing food item data if in edit mode
  useEffect(() => {
    const fetchFoodItem = async () => {
      if (!isEditing) return;
      
      setLoading(true);
      try {
        const docRef = doc(db, 'fooditems', foodItemId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setName(data.name);
          setDescription(data.description || '');
          setImageUrl(data.imageUrl || '');
          setCoins(data.coins || '');
          
          // Ensure categories are loaded before finding the matching one
          const currentCategory = categories.find(c => c.name === data.categoryName);
          if (currentCategory) {
            setSelectedCategory(currentCategory);
          }
        } else {
          setError("Food item not found.");
          navigate('/food-items');
        }
      } catch (err) {
        setError("Failed to fetch food item data.");
      } finally {
        setLoading(false);
      }
    };
    // Run this effect only when categories have loaded and we are in edit mode
    if (categories.length > 0 && isEditing) {
      fetchFoodItem();
    }
  }, [foodItemId, isEditing, categories, navigate]);


  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImageUrl(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ================================================================
    // === 3. Add the permission check before any database operation  ===
    // ================================================================
    if (isEditing && !hasPermission('food_items.update')) {
      return setError("You do not have permission to update food items.");
    }
    if (!isEditing && !hasPermission('food_items.create')) {
      return setError("You do not have permission to create food items.");
    }

    // Updated validation to include coins
    if (!name || !selectedCategory.id || coins === '') {
      setError('Name, Category, and Coins are required.');
      return;
    }
    setLoading(true);
    setError(''); // Clear previous errors
    
    const foodItemData = {
      name,
      description,
      imageUrl,
      categoryId: selectedCategory.name, // Storing name as ID as per your existing code
      categoryName: selectedCategory.name,
      coins: String(coins), // Save as a string to match your database schema
      updatedAt: serverTimestamp(),
    };

    try {
      if (isEditing) {
        const docRef = doc(db, 'fooditems', foodItemId);
        await updateDoc(docRef, foodItemData);
        alert('Food Item updated successfully!');
      } else {
        foodItemData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'fooditems'), foodItemData);
        alert('Food Item saved successfully!');
      }
      navigate('/food-items');
    } catch (err) {
      setError(`Failed to save item: ${err.message}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return <div className="status-message">Loading Food Item...</div>;
  }

  return (
    <div className="form-container">
      <h2>{isEditing ? 'Edit Food Item' : 'Add New Food Item'}</h2>
      <form onSubmit={handleSubmit} className="item-form">
        {error && <p className="error-message">{error}</p>}
        
        <div className="form-group">
          <label>Category</label>
          <select 
            className="input-field" 
            value={selectedCategory.id || ''}
            onChange={(e) => {
                const cat = categories.find(c => c.id === e.target.value) || { id: '', name: '' };
                setSelectedCategory(cat);
            }} 
            required
          >
            <option value="">-- Select a Category --</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Food Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input-field" required />
        </div>

        <div className="form-group">
          <label>Coins</label>
          <input 
            type="number" 
            value={coins} 
            onChange={(e) => setCoins(e.target.value)} 
            className="input-field" 
            required 
            min="0"
          />
        </div>

        <div className="form-group">
          <label>Description (Optional)</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="input-field" rows="4"></textarea>
        </div>
        
        <div className="form-group">
            <label>Image</label>
            <input type="file" accept="image/*" onChange={handleImageChange} className="input-field" />
            {imageUrl && <img src={imageUrl} alt="Preview" className="image-preview"/>}
        </div>

        <div className="form-actions">
            <button type="button" className="cancel-button" onClick={() => navigate('/food-items')} disabled={loading}>Cancel</button>
            <button type="submit" disabled={loading} className="submit-button">
                {loading ? 'Saving...' : (isEditing ? 'Update Item' : 'Save Item')}
            </button>
        </div>
      </form>
    </div>
  );
}

export default AddFoodItemPage;